import React, { useContext, useState, useEffect } from 'react';
import { Form, Badge, Button, Alert, Card, Accordion, Container } from 'react-bootstrap';
import ClientCard from './ClientCard';
import FournisseurCard from './FournisseurCard';
import CarSelector from './CarSelector';
import NotesSection from './NotesSection';
import { postAPI, putAPI, deleteAPI } from '../../../../apis/backend/apiNode';
import AuthContext from '../../../../contexts/authContext';
import Icon from '../../../../components/icon/Icon';
import showNotification from '../../../../components/extras/showNotification';
import '../styles/DetailsTab.css';

const DetailsTab = ({
	intervention,
	cars,
	formatDateTime,
	getUserDisplayName,
	reloadData,
	setSelectedIntervention
}) => {
	const { userData } = useContext(AuthContext);
	const isTechnician = userData?.function?.name?.includes('Technicien')
	const [attachments, setAttachments] = useState(intervention?.attachments || []);
	const [filesToUpload, setFilesToUpload] = useState([]);
	const [deletingAttachment, setDeletingAttachment] = useState(null);
	const [uploadingAttachments, setUploadingAttachments] = useState(false);
	const [poseFormData, setPoseFormData] = useState({
		date_debut: '',
		date_fin: '',
		selectedCars: [],
		notes: '',
	});

	const [creatingPose, setCreatingPose] = useState(false);
	const [poseCreationError, setPoseCreationError] = useState(null);
	const [poseCreationSuccess, setPoseCreationSuccess] = useState(null);

	const isEnlevement = intervention?.type === 'enlevement';
	const client = intervention?.client;
	const fournisseur = intervention?.fournisseur;
	const fiche = intervention?.fiche; // Get fiche from intervention

	useEffect(() => {
		if (intervention?.attachments) {
			setAttachments(intervention.attachments);
		}
		console.log('intervention from details tab status : ', intervention?.statut);
		console.log('intervention from details tab techniciens : ', intervention?.techniciens);
	}, [intervention]);

	const handleFileChange = (e) => {
		const files = Array.from(e.target.files);
		setFilesToUpload((prev) => [...prev, ...files]);
	};

	const removeFileFromUpload = (index) => {
		setFilesToUpload((prev) => prev.filter((_, i) => i !== index));
	};

	const uploadAndUpdateAttachments = async () => {
		if (filesToUpload.length === 0 || !intervention?._id) return true;

		setUploadingAttachments(true);
		try {
			const formData = new FormData();
			filesToUpload.forEach((file) => {
				formData.append('attachments', file);
			});

			const uploadResponse = await postAPI(
				'attachment',
				formData,
				`/intervention/${intervention._id}`,
			);

			if (uploadResponse?.files) {
				const formattedFiles = uploadResponse.files.map((file) => ({
					originalName: file.originalName || file.name,
					fileName:
						file.fileName ||
						`att_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
					mimeType: file.mimeType || file.type || 'application/octet-stream',
					size: file.size || 0,
					path:
						file.path ||
						`uploads/interventions/${intervention._id}/${file.fileName || file.name}`,
					uploadedAt: new Date().toISOString(),
					uploadedBy: userData._id || userData.id,
				}));

				const updateResponse = await putAPI(
					'planning',
					{
						operation: 'add',
						newAttachments: formattedFiles,
						userId: userData._id || userData.id,
					},
					`/intervention/${intervention._id}/attachments`,
				);

				if (updateResponse?.success) {
					const newAttachments = [...attachments, ...formattedFiles];
					setAttachments(newAttachments);
					setFilesToUpload([]);

					showNotification(
						'Succès',
						`${uploadResponse.files?.length || 0} fichier(s) ajouté(s) avec succès`,
						'success',
					);
					return true;
				} else {
					showNotification(
						'Avertissement',
						'Fichiers téléchargés, mais échec de la mise à jour des pièces jointes',
						'warning',
					);
					return false;
				}
			} else {
				showNotification(
					'Avertissement',
					'Échec du téléchargement des pièces jointes',
					'warning',
				);
				return false;
			}
		} catch (error) {
			console.error('❌ Error during attachment upload/update:', error);
			showNotification(
				'Erreur',
				'Erreur lors du téléchargement des pièces jointes',
				'danger',
			);
			return false;
		} finally {
			setUploadingAttachments(false);
		}
	};

	const deleteAttachment = async (attachmentId, attachmentIndex) => {
		if (!attachmentId || !intervention?._id) return;

		setDeletingAttachment(attachmentId);
		try {
			await deleteAPI('attachment', `/${attachmentId}`);

			const deleteResponse = await deleteAPI(
				'planning',
				`/${intervention._id}/attachments/${attachmentId}`,
				{
					userId: userData._id || userData.id,
				},
			);

			if (deleteResponse?.success) {
				const newAttachments = attachments.filter(
					(att, idx) => idx !== attachmentIndex && att._id !== attachmentId,
				);

				setAttachments(newAttachments);
				showNotification('Succès', 'Fichier supprimé avec succès', 'success');
			} else {
				throw new Error('Failed to delete attachment from intervention');
			}
		} catch (e) {
			console.error('Delete error:', e);

			// Fallback
			try {
				const newAttachments = attachments.filter(
					(att, idx) => idx !== attachmentIndex && att._id !== attachmentId,
				);

				const updateResponse = await putAPI(
					'planning',
					{
						operation: 'replace',
						attachments: newAttachments,
						userId: userData._id || userData.id,
					},
					`/intervention/${intervention._id}/attachments`,
				);

				if (updateResponse?.success) {
					setAttachments(newAttachments);
					showNotification('Succès', 'Fichier supprimé avec succès', 'success');
				} else {
					throw new Error('Fallback also failed');
				}
			} catch (fallbackError) {
				console.error('Fallback delete error:', fallbackError);
				showNotification('Erreur', 'Échec de la suppression du fichier', 'danger');
			}
		} finally {
			setDeletingAttachment(null);
		}
	};

	const formatTechnicianInfo = (techniciens) => {
		if (!techniciens || techniciens.length === 0) return null;

		return techniciens.map((tech, index) => (
			<div key={index} className='mb-2'>
				<div className='d-flex align-items-center'>
					<Icon icon='Person' className='me-2 text-primary' />
					<div>
						<strong className='text-dark'>{tech.name || tech.userName}</strong>
					</div>
				</div>
			</div>
		));
	};

	const getFileIcon = (fileName) => {
		const ext = fileName.split('.').pop().toLowerCase();
		const iconMap = {
			pdf: { icon: 'FilePdf', color: 'danger' },
			doc: { icon: 'FileWord', color: 'primary' },
			docx: { icon: 'FileWord', color: 'primary' },
			xls: { icon: 'FileExcel', color: 'success' },
			xlsx: { icon: 'FileExcel', color: 'success' },
			ppt: { icon: 'FilePowerpoint', color: 'warning' },
			pptx: { icon: 'FilePowerpoint', color: 'warning' },
			jpg: { icon: 'Image', color: 'info' },
			jpeg: { icon: 'Image', color: 'info' },
			png: { icon: 'Image', color: 'info' },
			gif: { icon: 'Image', color: 'info' },
			txt: { icon: 'FileAlt', color: 'secondary' },
			zip: { icon: 'FileArchive', color: 'warning' },
			rar: { icon: 'FileArchive', color: 'warning' },
		};

		const iconConfig = iconMap[ext] || { icon: 'File', color: 'muted' };

		return (
			<Icon
				icon={iconConfig.icon}
				color={iconConfig.color === 'muted' ? undefined : iconConfig.color}
				className={iconConfig.color === 'muted' ? 'text-muted' : undefined}
				size='sm'
			/>
		);
	};

	const formatFileSize = (bytes) => {
		if (!bytes || bytes === 0) return '0 Bytes';
		const k = 1024;
		const sizes = ['Bytes', 'KB', 'MB', 'GB'];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
	};

	const getFileUrl = (attachment) => {
		if (attachment.path && !attachment.path.startsWith('blob:')) {
			return `/coreService/${attachment.path}`;
		}
		return '#';
	};

	const renderFileItem = (attachment, index, isUpload = false) => {
		const fileName = attachment.originalName || attachment.name;
		const fileUrl = isUpload ? '#' : getFileUrl(attachment);
		const isImage = fileName.toLowerCase().match(/\.(jpg|jpeg|png|gif|bmp|webp)$/);

		return (
			<div key={index} className='border rounded p-3 bg-white mb-3 shadow-sm'>
				<div className='d-flex align-items-center justify-content-between'>
					<div className='d-flex align-items-center'>
						<div className='me-3'>{getFileIcon(fileName)}</div>
						<div>
							<div className='fw-medium small' title={fileName}>
								{fileName}
							</div>
							<div className='text-muted extra-small'>
								{formatFileSize(attachment.size)}
							</div>
						</div>
					</div>

					<div className='d-flex gap-1'>
						{!isUpload && fileUrl !== '#' && (
							<Button
								variant='outline-primary'
								size='sm'
								href={fileUrl}
								target='_blank'
								rel='noreferrer'
								title='Ouvrir'>
								<Icon icon='Link' size='sm' />
							</Button>
						)}
						{!isTechnician && (
							<>
								{isUpload ? (
									<Button
										variant='outline-danger'
										size='sm'
										onClick={() => removeFileFromUpload(index)}
										disabled={uploadingAttachments}
										title='Supprimer'>
										<Icon icon='Times' size='sm' />
									</Button>
								) : (
									<Button
										variant='outline-danger'
										size='sm'
										onClick={() =>
											deleteAttachment(
												attachment._id || attachment.path,
												index,
											)
										}
										disabled={
											deletingAttachment ===
											(attachment._id || attachment.path)
										}
										title='Supprimer'>
										{deletingAttachment ===
											(attachment._id || attachment.path) ? (
											<span
												className='spinner-border spinner-border-sm'
												role='status'></span>
										) : (
											<Icon icon='Trash' size='sm' />
										)}
									</Button>
								)}
							</>
						)}

						{!isUpload && fileUrl !== '#' && (
							<Button
								variant='outline-success'
								size='sm'
								href={fileUrl}
								download={fileName}
								title='Télécharger'>
								<Icon icon='Download' size='sm' />
							</Button>
						)}
					</div>
				</div>

				{isImage && fileUrl !== '#' && (
					<div className='mt-3'>
						<img
							src={fileUrl}
							alt={fileName}
							className='img-fluid rounded border'
							style={{ maxHeight: '200px', objectFit: 'cover' }}
							onError={(e) => {
								console.warn(`Impossible de charger l'image: ${fileUrl}`);
								e.target.style.display = 'none';

								const container = e.target.parentElement;
								container.innerHTML = `
                  <div class="text-center py-4">
                    <i class="fas fa-image fa-2x text-muted mb-2"></i>
                    <p class="small text-muted mb-0">Image non disponible</p>
                  </div>
                `;
							}}
						/>
					</div>
				)}
			</div>
		);
	};

	// Fonctions pour la pose
	const handlePoseFormChange = (field, value) =>
		setPoseFormData((prev) => ({ ...prev, [field]: value }));

	const handleToggleCarPose = (carId) => {
		setPoseFormData((prev) => ({
			...prev,
			selectedCars: prev.selectedCars.includes(carId)
				? prev.selectedCars.filter((id) => id !== carId)
				: [...prev.selectedCars, carId],
		}));
	};

	const handleSubmitPose = async () => {
		try {
			setCreatingPose(true);
			setPoseCreationError(null);

			if (!poseFormData.date_debut || !poseFormData.date_fin)
				throw new Error('Dates requises');
			if (poseFormData.selectedCars.length === 0) throw new Error('Véhicule requis');

			const response = await postAPI(
				'planning',
				{
					date_debut: poseFormData.date_debut,
					date_fin: poseFormData.date_fin,
					cars: poseFormData.selectedCars,
					notes: poseFormData.notes,
					userId: userData._id || userData.id,
				},
				`/intervention/${intervention._id}/creer-pose`,
			);

			if (response.data.success) {
				setPoseCreationSuccess({
					message: 'Pose créée !',
					reference: response.data.data.reference,
				});
				setTimeout(() => window.location.reload(), 1500);
			}
		} catch (error) {
			setPoseCreationError(error.message || 'Erreur');
		} finally {
			setCreatingPose(false);
		}
	};

	const getStatusVariant = (s) => {
		if (!s) return 'secondary';
		return (
			{ planifie: 'info', en_cours: 'warning', termine: 'success', annule: 'danger' }[s] ||
			'secondary'
		);
	};

	const currentStatus = intervention.statut || 'N/A';
	const isDevisAccepted =
		intervention?.rapport?.devis?.statut === 'accepté' && intervention?.type === 'devis';
	const hasHierarchy = intervention.intervention_parent || intervention.children?.length > 0;
	
	return (
		<Container className='ps-4 py-4 row d-flex justify-content-between'>
			<div className='row d-flex col-lg-6 col-md-6 col-12'>
				<Card className='mb-4 border shadow-sm'>
					<Card.Header className='bg-white py-3 border-bottom d-flex justify-content-between align-items-center'>
						<h5 className='mb-0 fw-bold d-flex align-items-center text-dark'>
							<Icon icon='Info' className='me-2 text-primary' /> Détails Opérationnels
						</h5>
						{isEnlevement && (
							<Badge bg='warning' className='d-flex align-items-center'>
								<Icon icon='LocalShipping' size='xs' className='me-1' />
								Enlèvement chez fournisseur
							</Badge>
						)}
					</Card.Header>
					<Card.Body className='p-0'>
						<div className='py-3 px-4 border-bottom'>
							<div className='d-flex align-items-center text-capitalize'>
								<Icon icon='Category' size='s' className='me-2 text-muted' />
								<span className='text-muted me-2'>Société:</span>
								{intervention?.companyId?.name || intervention?.cars?.[0]?.companyId?.name}
							</div>
						</div>
						<div className='py-3 px-4 border-bottom'>
							<div className='d-flex align-items-center text-capitalize'>
								<Icon icon='Category' size='s' className='me-2 text-muted' />
								<span className='text-muted me-2'>Type:</span>
								{intervention.type} - {intervention.motif}
							</div>
						</div>
						<div className='py-3 px-4 border-bottom'>
							<div className='d-flex align-items-center'>
								<Icon icon='CalendarToday' className='me-2 text-muted' />
								<span className='text-muted me-2'>Date:</span>
								<span className='fw-medium text-dark'>
									{formatDateTime(intervention.date_debut) || 'Non définie'} -{' '}
									{formatDateTime(intervention.date_fin) || 'Non définie'}
								</span>
							</div>
						</div>
						<div className='py-3 px-4 '>
							<div className='d-flex align-items-center'>
								<Icon icon='CalendarToday' className='me-2 text-muted' />
								<span className='text-muted me-2'>Créé par :</span>
								<span className='fw-medium text-dark'>
									{intervention?.createdBy?.name}
								</span>
							</div>
						</div>
					</Card.Body>
				</Card>

				{!isTechnician && (
					<Card className='mb-4 border shadow-sm'>
						<Card.Header className='bg-white py-3 border-bottom d-flex justify-content-between align-items-center'>
							<h5 className='mb-0 fw-bold d-flex align-items-center text-dark'>
								<Icon icon='DirectionsCar' className='me-2 text-primary' /> Véhicules
								Assignés
							</h5>
							<Badge bg='info' className='d-flex align-items-center'>
								<Icon icon='Check' size='xs' className='me-1' />
								{intervention.cars?.length || 0} véhicule(s)
							</Badge>
						</Card.Header>
						<Card.Body className='p-3 bg-light bg-opacity-10'>
							<CarSelector
								cars={intervention.cars}
								selectedCars={intervention.cars}
								onToggleCar={() => { }}
								isEditing={false}
								showOnlySelected={true}
							/>
						</Card.Body>
					</Card>
				)}

				{/* 6. TECHNICIENS */}
				{intervention?.statut === 'termine' &&
					intervention?.techniciens &&
					intervention.techniciens.length > 0 && (
						<Card className='mb-4 border shadow-sm'>
							<Card.Header className='bg-white py-3 border-bottom d-flex justify-content-between align-items-center'>
								<h5 className='mb-0 fw-bold d-flex align-items-center text-dark'>
									<Icon icon='PersonCheck' className='me-2 text-success' />{' '}
									Technicien(s) ayant réalisé l'intervention
								</h5>
								<Badge bg='success' className='d-flex align-items-center'>
									<Icon icon='CheckCircle' size='xs' className='me-1' />
									{intervention.techniciens.length} technicien(s)
								</Badge>
							</Card.Header>
							<Card.Body className='p-4'>
								<div className='bg-light p-3 rounded border'>
									{formatTechnicianInfo(intervention.techniciens)}
								</div>

								<div className='mt-3 text-muted small'>
									<Icon icon='InfoCircle' className='me-1' />
									Cette intervention a été réalisée par le(s) technicien(s) ci-dessus.
								</div>
							</Card.Body>
						</Card>
					)}

				{/* 7. PIÈCES JOINTES */}
				<Card className='mb-4 border shadow-sm'>
					<Card.Header className='bg-white py-3 border-bottom d-flex justify-content-between align-items-center'>
						<div className='d-flex align-items-center'>
							<Icon icon='AttachFile' className='me-2 text-primary' />
							<h5 className='mb-0 fw-bold text-dark'>Pièces Jointes</h5>
						</div>
						<div className='d-flex align-items-center gap-2'>
							{!isTechnician && (
								<>
									<Button
										variant='outline-primary'
										size='sm'
										onClick={() =>
											document.getElementById('fileInput').click()
										}>
										<Icon icon='Plus' className='me-1' />
										Ajouter
									</Button>
									<input
										type='file'
										id='fileInput'
										multiple
										onChange={handleFileChange}
										style={{ display: 'none' }}
									/>
									<Badge bg='info'>
										{attachments?.length + filesToUpload?.length} fichier(s)
									</Badge>
								</>
							)}
						</div>
					</Card.Header>
					<Card.Body>
						{filesToUpload?.length > 0 && (
							<div className='mb-4'>
								<h6 className='text-primary fw-bold mb-2'>
									<Icon icon='Upload' className='me-2' />
									Fichiers à télécharger ({filesToUpload.length})
								</h6>
								{filesToUpload.map((file, index) =>
									renderFileItem(file, index, true),
								)}

								<div className='mt-3'>
									<Button
										variant='primary'
										onClick={uploadAndUpdateAttachments}
										disabled={
											uploadingAttachments || filesToUpload.length === 0
										}
										className='px-4 me-2'>
										{uploadingAttachments ? (
											<>
												<span
													className='spinner-border spinner-border-sm me-2'
													role='status'></span>
												Téléchargement...
											</>
										) : (
											<>
												<Icon icon='CloudUpload' className='me-2' />
												Télécharger les fichiers
											</>
										)}
									</Button>
									<Button
										variant='outline-secondary'
										onClick={() => setFilesToUpload([])}
										disabled={uploadingAttachments}>
										Annuler
									</Button>
								</div>
							</div>
						)}

						{attachments?.length > 0 ? (
							<div className='mt-4'>
								<h6 className='text-success fw-bold mb-3'>
									<Icon icon='AttachFile' className='me-2' />
									Fichiers attachés ({attachments?.length})
								</h6>
								<div className='row g-3'>
									{attachments?.map((att, index) => (
										<div key={index} className='col-12 col-md-6 col-lg-4'>
											{renderFileItem(att, index, false)}
										</div>
									))}
								</div>
							</div>
						) : (
							<div className='text-center py-5 text-muted bg-light rounded'>
								<Icon icon='FolderOpen' size='lg' className='mb-3' />
								<div className='fw-medium'>Aucun fichier attaché</div>
								<small className='text-muted'>
									Ajoutez des fichiers en cliquant sur le bouton "Ajouter"
								</small>
							</div>
						)}
					</Card.Body>
				</Card>
			</div>
			
			<div className='row d-flex col-lg-6 col-md-6 col-12'>
				{/* 3. CARTE CLIENT OU FOURNISSEUR */}
				{isEnlevement ? (
					<FournisseurCard fournisseur={fournisseur} />
				) : (
					<ClientCard client={client} intervention={intervention} setSelectedIntervention={(result) => {
						setSelectedIntervention({ ...intervention, client: result });
					}} />
				)}

				{/* NEW: Fiche Card - Display selected fiche */}
				{fiche && (
					<Card className='mb-4 border shadow-sm'>
						<Card.Header className='bg-white py-3 border-bottom'>
							<h5 className='mb-0 fw-bold d-flex align-items-center text-dark'>
								<Icon icon='Description' className='me-2 text-primary' /> 
								Fiche Google
							</h5>
						</Card.Header>
						<Card.Body>
							<div className='d-flex align-items-start gap-3'>
								<div className='bg-success bg-opacity-10 rounded-circle p-2'>
									<Icon icon='Store' className='text-success' />
								</div>
								<div className='flex-grow-1'>
									<h6 className='fw-bold mb-1'>{fiche.name}</h6>
									{fiche.formatted_address && (
										<div className='text-muted small mb-2'>
											<Icon icon='LocationOn' size='xs' className='me-1' />
											{fiche.formatted_address}
										</div>
									)}
									{fiche.place_id && (
										<div className='mt-2'>
											<Badge bg='info' className='me-2'>
												Place ID: {fiche.place_id}
											</Badge>
											<a
												href={`https://search.google.com/local/writereview?placeid=${fiche.place_id}`}
												target='_blank'
												rel='noopener noreferrer'
												className='btn btn-sm btn-outline-primary mt-2'
											>
												<Icon icon='Link' size='xs' className='me-1' />
												Lien avis Google
											</a>
										</div>
									)}
								</div>
							</div>
						</Card.Body>
					</Card>
				)}

				{/* 10. NOTES */}
				<NotesSection
					reloadData={reloadData}
					notes={intervention.notes}
					interventionId={intervention._id}
					formatDateTime={formatDateTime}
					getUserDisplayName={getUserDisplayName}
				/>
			</div>

			{/* 8. PLANIFICATION POSE */}
			{!isTechnician && isDevisAccepted && (
				<div className='mb-4'>
					<Accordion className='border rounded shadow-sm bg-white'>
						<Accordion.Item eventKey='0' className='border-0'>
							<Accordion.Header>
								<div className='d-flex align-items-center text-primary w-100 py-1'>
									<div className='bg-primary bg-opacity-10 rounded-circle p-2 me-3'>
										<Icon icon='AddCircle' size='lg' />
									</div>
									<div>
										<strong className='d-block text-dark'>
											Planifier une Pose
										</strong>
										<span className='small text-muted'>
											Créer une intervention enfant basée sur ce devis
										</span>
									</div>
								</div>
							</Accordion.Header>
							<Accordion.Body className='bg-light p-4 border-top'>
								{poseCreationError && (
									<Alert variant='danger' className='border-0 shadow-sm'>
										<Icon icon='Error' className='me-2' />
										{poseCreationError}
									</Alert>
								)}
								{poseCreationSuccess && (
									<Alert variant='success' className='border-0 shadow-sm'>
										<Icon icon='CheckCircle' className='me-2' />
										{poseCreationSuccess.message}
									</Alert>
								)}

								<div className='bg-white p-4 rounded shadow-sm border'>
									<div className='row g-3 mb-4'>
										<div className='col-md-6'>
											<label className='small mb-1 fw-bold'>
												Date de Début
											</label>
											<Form.Control
												type='datetime-local'
												value={poseFormData.date_debut}
												onChange={(e) =>
													handlePoseFormChange(
														'date_debut',
														e.target.value,
													)
												}
											/>
										</div>
										<div className='col-md-6'>
											<label className='small mb-1 fw-bold'>
												Date de Fin
											</label>
											<Form.Control
												type='datetime-local'
												value={poseFormData.date_fin}
												onChange={(e) =>
													handlePoseFormChange('date_fin', e.target.value)
												}
											/>
										</div>
									</div>

									<div className='mb-4'>
										<label className='small mb-2 fw-bold d-flex justify-content-between'>
											<span>Véhicules pour la pose</span>
											<Badge bg='secondary'>
												{poseFormData.selectedCars.length} sélectionné(s)
											</Badge>
										</label>
										<div className='border p-3 rounded bg-light'>
											<CarSelector
												cars={cars}
												selectedCars={poseFormData.selectedCars}
												onToggleCar={handleToggleCarPose}
												isEditing={true}
											/>
										</div>
									</div>

									<Form.Group className='mb-4'>
										<label className='small mb-1 fw-bold'>
											Notes spécifiques
										</label>
										<Form.Control
											as='textarea'
											rows={2}
											value={poseFormData.notes}
											onChange={(e) =>
												handlePoseFormChange('notes', e.target.value)
											}
											placeholder='Instructions pour la pose...'
										/>
									</Form.Group>

									<div className='d-flex justify-content-end'>
										<Button
											variant='primary'
											onClick={handleSubmitPose}
											disabled={creatingPose}
											className='px-4'>
											{creatingPose
												? 'Création en cours...'
												: 'Confirmer la Planification'}
										</Button>
									</div>
								</div>
							</Accordion.Body>
						</Accordion.Item>
					</Accordion>
				</div>
			)}

			{/* 9. HIÉRARCHIE */}
			{!isTechnician && hasHierarchy && (
				<Card className='mb-4 border shadow-sm'>
					<Card.Header className='bg-white py-3 border-bottom'>
						<h5 className='mb-0 fw-bold d-flex align-items-center text-dark'>
							<Icon icon='AccountTree' className='me-2 text-primary' /> Contexte &
							Historique
						</h5>
					</Card.Header>
					<Card.Body className='p-0'>
						<div className='p-4 border-bottom'>
							<h6 className='fw-bold text-muted text-uppercase mb-3 small'>
								Interventions Liées
							</h6>
							<div className='d-flex flex-column gap-2'>
								{intervention.intervention_parent && (
									<div className='d-flex align-items-center justify-content-between p-3 bg-light rounded border'>
										<div className='d-flex align-items-center'>
											<Badge bg='secondary' className='me-3'>
												PARENT
											</Badge>
											<span className='fw-bold text-dark'>
												{intervention.intervention_parent.reference}
											</span>
										</div>
										<Icon icon='ArrowUpward' className='text-muted' />
									</div>
								)}
								{intervention.children?.map((child) => (
									<div
										key={child._id}
										className='d-flex align-items-center justify-content-between p-3 bg-light rounded border'>
										<div className='d-flex align-items-center'>
											<Badge bg='info' className='me-3'>
												ENFANT
											</Badge>
											<span className='fw-bold text-dark'>
												{child.reference}
											</span>
										</div>
										<div className='d-flex align-items-center gap-2'>
											<Badge
												bg={getStatusVariant(child.statut)}
												className='fw-normal'>
												{child.statut}
											</Badge>
											<Icon icon='ArrowDownward' className='text-muted' />
										</div>
									</div>
								))}
							</div>
						</div>
					</Card.Body>
				</Card>
			)}

			<div className='text-muted mt-1 small'>
				Créé le {formatDateTime(intervention.createdAt)}
				{intervention.updatedAt &&
					` • Mis à jour le ${formatDateTime(intervention.updatedAt)}`}
			</div>
		</Container>
	);
};

export default DetailsTab;